import React, { useState, useEffect } from 'react';
import { FcSearch } from 'react-icons/fc';
import { InfinitySpin } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { api_url } from '../../../config';
import api from '../../../utils/api';
import { toast } from 'react-toastify';
import { getPageNumbers } from '../../../utils/getPageNumbers';

const SelectProducts = ({ supplier, onProductsSelected }) => {
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [limit] = useState(100);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState();
    const [metadata, setMetadata] = useState('');
    const [search, setSearch] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);

    const { id, branch_id, token } = useSelector((state) => state.auth);

    // Client-side search
    useEffect(() => {
        if (search.trim() === '') {
            setFilteredProducts(products);
        } else {
            const filtered = products.filter(product => 
                product.product_name?.toLowerCase().includes(search.toLowerCase()) ||
                product.manufacturer?.toLowerCase().includes(search.toLowerCase())
            );
            setFilteredProducts(filtered);
        }
    }, [search, products]);

    const handleContinue = () => {
        if (selectedProducts.length === 0) {
            toast.warning('Please select at least one product to proceed');
            return;
        }
        onProductsSelected(selectedProducts);
    };

    const fetchProducts = async (page, limit) => {
        setLoading(true);
        if (!navigator.onLine) {
            toast("You're offline");
            setLoading(false);
            return;
        }
        const params = {
            page: page || 1,
            limit: limit || 50,
        };
        try {
            const response = await api.get(api_url + `/api/v1/retailer/user/${id}/retailbranch/${branch_id}/wholesalebranch/${supplier?.branch_id}/inventory`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                params
            });
            const data = response.data.data;
            const meta = response.data.meta;
            setProducts(data);
            setFilteredProducts(data);
            setMetadata(meta);
            setLastPage(meta.last_page);
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts(currentPage, limit);
    }, [currentPage, supplier]);

    const handleQuantityChange = (product, quantity) => {
        if (quantity < 0) quantity = 0;
        if (quantity > product.total) quantity = product.total;

        setSelectedProducts(prevSelected => {
            const updatedProducts = [...prevSelected];
            const index = updatedProducts.findIndex(p => p.id === product.id);
            if (index !== -1) {
                updatedProducts[index] = { ...updatedProducts[index], quantity };
            } else {
                updatedProducts.push({
                    id: product?.id,
                    name: product?.product_name,
                    manufacturer: product?.manufacturer,
                    quantity
                });
            }
            return updatedProducts.filter(p => p.quantity > 0);
        });
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleNextPage = () => {
        if (currentPage < lastPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    return (
        <div className="container mx-auto px-4">
            {loading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                    <InfinitySpin color="#059212" loading={loading} size={40} />
                </div>
            )}
            
            <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4'>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <h2 className="text-lg font-semibold text-gray-800 truncate max-w-[200px] sm:max-w-none">{supplier?.name}</h2>
                    <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex-shrink-0">
                        {filteredProducts.length} Products
                    </span>
                </div>
                <div className='flex items-center gap-2'>
                    <div className="relative">
                    <input
                        type='text'
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                            placeholder='Search products...'
                            className='w-38 sm:w-[250px] focus:outline-none rounded-md shadow-sm border px-4 py-2.5 h-10 font-lufga text-sm pl-10'
                        />
                        <FcSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                <thead>
                            <tr className="bg-gray-50 border-b">
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">Product Name</th>
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">Manufacturer</th>
                                <th className="py-3 px-4 text-right text-sm font-medium text-gray-600">Available</th>
                                <th className="py-3 px-4 text-right text-sm font-medium text-gray-600">Quantity</th>
                    </tr>
                </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredProducts.map(product => (
                                <tr key={product?.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="py-3 px-4 text-sm text-gray-900">{product?.product_name}</td>
                                    <td className="py-3 px-4 text-sm text-gray-600">{product?.manufacturer}</td>
                                    <td className="py-3 px-4 text-sm text-right text-gray-600">{product?.total}</td>
                                    <td className="py-3 px-4">
                                <div className="flex justify-end items-center">
                                    <button
                                                className="bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-l-md transition-colors"
                                        onClick={() => handleQuantityChange(product, (selectedProducts.find(p => p.id === product.id)?.quantity || 0) - 1)}
                                    >
                                        -
                                    </button>
                                    <input
                                        type="number"
                                        min="0"
                                        max={product.total}
                                        value={selectedProducts.find(p => p.id === product.id)?.quantity || 0}
                                                onChange={(e) => handleQuantityChange(product, parseInt(e.target.value) || 0)}
                                                className="w-16 text-center border-t border-b focus:outline-none focus:ring-1 focus:ring-green-500"
                                    />
                                    <button
                                                className="bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-r-md transition-colors"
                                        onClick={() => handleQuantityChange(product, (selectedProducts.find(p => p.id === product.id)?.quantity || 0) + 1)}
                                    >
                                        +
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
                </div>

                {/* Mobile Grid View */}
                <div className="md:hidden">
                    {filteredProducts.map(product => (
                        <div key={product?.id} className="p-4 border-b border-gray-200 bg-white hover:bg-gray-50 transition-colors">
                            <div className="flex flex-col gap-3">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <h3 className="text-sm font-medium text-gray-900">{product?.product_name}</h3>
                                        <p className="text-xs text-gray-600 mt-1">{product?.manufacturer}</p>
                                    </div>
                                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                        {product?.total} available
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">Select Quantity</span>
                                    <div className="flex items-center">
                                        <button
                                            className="bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-l-md transition-colors"
                                            onClick={() => handleQuantityChange(product, (selectedProducts.find(p => p.id === product.id)?.quantity || 0) - 1)}
                                        >
                                            -
                                        </button>
                                        <input
                                            type="number"
                                            min="0"
                                            max={product.total}
                                            value={selectedProducts.find(p => p.id === product.id)?.quantity || 0}
                                            onChange={(e) => handleQuantityChange(product, parseInt(e.target.value) || 0)}
                                            className="w-16 text-center border-t border-b focus:outline-none focus:ring-1 focus:ring-green-500"
                                        />
                                        <button
                                            className="bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-r-md transition-colors"
                                            onClick={() => handleQuantityChange(product, (selectedProducts.find(p => p.id === product.id)?.quantity || 0) + 1)}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {filteredProducts.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center bg-gray-50 rounded-lg border border-gray-200">
                    <div className="w-24 h-24 mb-4 text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Found</h3>
                    <p className="text-gray-600 mb-6 max-w-sm">
                        {search ? "No products match your search criteria." : "No products available from this supplier."}
                    </p>
                </div>
            )}

            <div className='flex items-center gap-4 mt-6 bg-gray-50 p-4 rounded-lg'>
                <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-md text-sm transition-colors ${
                        currentPage === 1 
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                            : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'
                    }`}
                >
                    Previous
                </button>
                <div className='flex items-center gap-2'>
                    {getPageNumbers(currentPage, lastPage).map((pageNumber, index) => (
                        <React.Fragment key={index}>
                            {pageNumber === '...' ? (
                                <span className="text-sm text-gray-500">...</span>
                            ) : (
                                <button
                                    onClick={() => handlePageClick(pageNumber)}
                                    className={`w-8 h-8 rounded-md text-sm transition-colors ${
                                        currentPage === pageNumber 
                                            ? 'bg-green-500 text-white shadow-sm' 
                                            : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'
                                    }`}
                                >
                                    {pageNumber}
                                </button>
                            )}
                        </React.Fragment>
                    ))}
                </div>
                <button
                    onClick={handleNextPage}
                    disabled={currentPage === lastPage}
                    className={`px-4 py-2 rounded-md text-sm transition-colors ${
                        currentPage === lastPage 
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                            : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'
                    }`}
                >
                    Next
                </button>
            </div>

            <div className='flex justify-between mt-6 bg-gray-50 p-4 rounded-lg'>
                <Link to='/retail/orders/addOrders/' state={{ requiredRoles: ['orderViewer', 'orderWriter', 'orderAdmin'] }}>
                    <button className="px-6 py-2 bg-white hover:bg-gray-100 text-gray-700 rounded-md shadow-sm text-sm transition-colors flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back
                    </button>
                </Link>

                <button
                    onClick={handleContinue}
                    className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md shadow-sm text-sm transition-colors flex items-center gap-2"
                >
                    Continue
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default SelectProducts;